import { createContext, useState, useContext, useEffect } from "react";
import { PropTypes } from "prop-types";
import getRecommendedOutfits from "../../api/recommendedOutfitsApi";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { setRecommendedOutfitsTracking } from "../../analytics/postPageLoad/recommendedOutfits";
import { logError, ERROR_TYPE } from "../../utils/newRelicLogger";

export const RecommendedOutfitsContext = createContext({});

const isRecommendedOutfitsFeatureEnabled = (product) => {
  const recommendedOutfitsFeature = getFeature("pdp-styled-for-you");
  const { productType: { id: productTypeId } = {} } = product;

  return (
    recommendedOutfitsFeature?.isEnabled() &&
    recommendedOutfitsFeature?.getVariables()?.recommended_outfits &&
    recommendedOutfitsFeature
      ?.getVariables()
      ?.product_type_ids?.includes(productTypeId)
  );
};

export const RecommendedOutfitsProvider = ({ children }) => {
  const [recommendedOutfits, setRecommendedOutfits] = useState([]);
  const { product, isMobile } = useRuntimeConstants();
  const [shouldShowRecommendedOutfits, setShouldShowRecommendedOutfits] =
    useState(false);
  const { areFeaturesReady } = useFeaturesReady();
  const [hasRecommendationApiLoaded, setHasRecommendationApiLoaded] =
    useState(false);
  const [isRecommendedOutfitsEnabled, setIsRecommendedOutfitsEnabled] =
    useState(false);

  useEffect(() => {
    if (areFeaturesReady) {
      setIsRecommendedOutfitsEnabled(
        isRecommendedOutfitsFeatureEnabled(product)
      );
    }
  }, [areFeaturesReady]);

  useEffect(() => {
    if (isMobile && areFeaturesReady && isRecommendedOutfitsEnabled) {
      setShouldShowRecommendedOutfits(true);
      getRecommendedOutfits({ product })
        .then((data) => {
          setHasRecommendationApiLoaded(true);
          if (data?.recommendations?.length) {
            setRecommendedOutfits(data.recommendations);
            setRecommendedOutfitsTracking({
              numOfRecProducts: data.recommendations.length,
            });
          } else {
            setShouldShowRecommendedOutfits(false);
            setRecommendedOutfitsTracking({
              numOfRecProducts: 0,
            });
          }
        })
        .catch((error) => {
          setRecommendedOutfitsTracking({
            numOfRecProducts: 0,
          });
          logError(ERROR_TYPE.UserActionFailed, {
            action: "getRecommendedOutfits",
            caller: "recommendedOutfits",
            apiName: "OutfitRecommendations",
            message: error.message,
          });
        });
    }
  }, [product, areFeaturesReady, isRecommendedOutfitsEnabled, isMobile]);

  if (!isRecommendedOutfitsEnabled || !isMobile) {
    setRecommendedOutfitsTracking({
      shouldTrack: false,
    });
  }

  return (
    <RecommendedOutfitsContext.Provider
      value={{
        recommendedOutfits,
        shouldShowRecommendedOutfits,
        hasRecommendationApiLoaded,
      }}
    >
      {children}
    </RecommendedOutfitsContext.Provider>
  );
};

export const useRecommendedOutfits = () =>
  useContext(RecommendedOutfitsContext);

RecommendedOutfitsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
